import { useCallback, useRef } from 'react';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { functionUnionWithCondition } from '@atlaskit/platform-feature-flags-react';

import { invite as inviteService } from '@atlassian/expansion-services/services';
import { showInviteFlags, type ShowInviteFlagsArgs } from '@atlassian/invite-on-third-party-link';
import { fetchEligibleEmails } from '@atlassian/invite-on-third-party-link/services/third-party-link/fetch-eligible-emails';
import { fetchPiSlackLinkEligibleUsers } from '@atlassian/invite-on-third-party-link/services/third-party-link/fetch-pi-slack-link-eligible-users';
import type { FetchEligibleEmailsResponse } from '@atlassian/invite-on-third-party-link/services/third-party-link/types';
import type { ShownInviteFlag } from '@atlassian/invite-on-third-party-link/types';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
import { usePageSpaceKey } from '@confluence/page-context';
import { useSpaceDetail } from '@confluence/space-utils/entry-points/useSpaceDetail.ts';
import { useService } from '@confluence/expansion-services';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import {
	getAllLinks,
	getChannelIdFromSlackLink,
	isMailtoLink,
	isSlackLink,
	getEmailsFromMailtoLink,
} from './utils/links.ts';
import { fetchIntegrationTraits } from './spotlight/useSpotlightTraits.ts';
import { check3pIn1pDismissState, set3pIn1pDismissState } from './spotlight/spotlightHelpers.ts';
import { is3pInviteEligible, getInviteOn3pDismissType } from './traits-helpers.tsx';
import type { IntegrationSpotlightTraits, ThirdPartyInviteType } from './traits-helpers.tsx';

// Gated hook wrappers — defined at module level so React's rules of hooks are
// satisfied (hook call order is stable) while avoiding the work when the gate is off.
const useInviteServiceGated = functionUnionWithCondition(
	() => fg('twcg-853-3p-in-1p-link-gate'),
	(service: typeof inviteService) => useService(service),
	() => null,
);

const usePageSpaceKeyGated = functionUnionWithCondition(
	() => fg('twcg-853-3p-in-1p-link-gate'),
	usePageSpaceKey,
	() => [undefined] as const,
);

const useSpaceDetailGated = functionUnionWithCondition(
	() => fg('twcg-853-3p-in-1p-link-gate'),
	useSpaceDetail,
	() => ({ data: undefined, loading: false }),
);

type PiEligibilityResult = Awaited<ReturnType<typeof fetchPiSlackLinkEligibleUsers>>;

/**
 * Classify a list of URLs into slack/mailto buckets, excluding any that appear
 * in `startingLinks`.
 */
const classifyNewLinks = (
	urls: Iterable<string>,
	startingLinks: Set<string>,
): { pastedSlackLinks: string[]; pastedMailtoLinks: string[] } => {
	const pastedSlackLinks: string[] = [];
	const pastedMailtoLinks: string[] = [];

	for (const link of urls) {
		if (startingLinks.has(link)) {
			continue;
		}
		if (isSlackLink(link)) {
			pastedSlackLinks.push(link);
		} else if (isMailtoLink(link)) {
			pastedMailtoLinks.push(link);
		}
	}

	return { pastedSlackLinks, pastedMailtoLinks };
};

export const useDetectPastedThirdPartyLinks = (flags?: FlagsStateContainer) => {
	const { createEvent } = useConfluenceAnalyticsEvents();
	const { userId, orgId, cloudId } = useSessionData();
	const [spaceKey] = usePageSpaceKeyGated();
	const { data: spaceDetail } = useSpaceDetailGated(spaceKey);
	const invite = useInviteServiceGated(inviteService);
	const inviteFlagSourcesRef = useRef<
		Map<string, { source: ShownInviteFlag['source']; dismissType: number }>
	>(new Map());

	// Traits are fetched lazily on the first paste that reaches triggerSpotlight,
	// then cached for the lifetime of the hook. This avoids an eager TAP network
	// request on every editor mount when no 3P link is ever pasted.
	// null = not yet fetched; {} = fetched (possibly empty on error/no traits)
	const invite3pTraitsCacheRef = useRef<IntegrationSpotlightTraits | null>(null);
	const invite3pTraitsFetchingRef = useRef(false);

	// Track what URLs existed when editor opened (initial state)
	// This enables URL-level delta detection
	const initialLinksRef = useRef<Set<string>>(new Set());

	/**
	 * Set the initial content when editor opens
	 */
	const setInitialLinks = useCallback((adfValue: ADFEntity | null | undefined) => {
		if (!(fg('twcg-853-3p-in-1p-aa-test-gate') || fg('twcg-853-3p-in-1p-link-gate')) || !adfValue) {
			return;
		}
		const initialLinks = getAllLinks(adfValue);
		initialLinksRef.current = initialLinks;
	}, []);

	const fireAnalytics = useCallback(
		({
			pastedSlackLinks,
			pastedMailtoLinks,
		}: {
			pastedSlackLinks: string[];
			pastedMailtoLinks: string[];
		}) => {
			// 3P in 1P exp eligibility check flow
			if (
				(pastedSlackLinks.length === 0 && pastedMailtoLinks.length === 0) ||
				!fg('twcg-853-3p-in-1p-aa-test-gate')
			) {
				return;
			}

			// Exposure fired here
			const twcg3pIn1pAATestCohort = expValEquals('twcg-853-3p-in-1p-aa-test', 'isEnabled', true)
				? 'variation'
				: 'control';

			createEvent({
				type: 'sendUIEvent',
				data: {
					action: 'pasted',
					actionSubject: '3PLink',
					actionSubjectId: 'editorPaste',
					source: 'inviteOnLinkInsert',
					attributes: {
						slackLinks: pastedSlackLinks,
						channelIds: pastedSlackLinks.map(getChannelIdFromSlackLink),
						mailtoLinkCount: pastedMailtoLinks.length,
						orgId,
						accountId: userId,
						twcg3pIn1pAATestCohort,
					},
				},
			}).fire();
		},
		[createEvent, userId, orgId],
	);

	const triggerSpotlight = useCallback(
		async (pastedSlackLinks: string[], pastedMailtoLinks: string[]) => {
			if (!fg('twcg-853-3p-in-1p-link-gate')) {
				return;
			}

			const fireSpotlightError = (
				stage: 'traitFetch' | 'slackEligibility' | 'emailEligibility' | 'unknown',
			) => {
				createEvent({
					type: 'sendOperationalEvent',
					data: {
						action: 'failed',
						actionSubject: 'inviteOn3rdPartyLinkSpotlight',
						source: 'inviteOnThirdPartyLinkFlag',
						attributes: {
							stage,
							product: 'confluence',
							growthInitiative: 'virality',
						},
						tags: ['measurement', 'growth', 'hasCustomAttributes'],
					},
				}).fire();
			};

			const hasThirdPartyInviteCandidates =
				pastedSlackLinks.length > 0 || pastedMailtoLinks.length > 0;
			if (
				hasThirdPartyInviteCandidates &&
				flags &&
				invite &&
				userId &&
				fg('twcg-853-3p-in-1p-link-gate')
			) {
				// ── TRAIT LAYER: mirrors IntegrationLinkSpotlightComponent LAYER 2 & 3 ──

				// LAYER 3: Per-type trait eligibility (stage + hard-dismiss check).
				// Traits are fetched lazily on the first paste to avoid an eager mount-time
				// TAP network request. If a fetch is already in flight, skip this paste and
				// let the next save/paste attempt use the cached result.
				if (invite3pTraitsCacheRef.current === null) {
					if (invite3pTraitsFetchingRef.current) {
						return;
					}
					invite3pTraitsFetchingRef.current = true;
					try {
						invite3pTraitsCacheRef.current = await fetchIntegrationTraits(userId ?? '');
					} catch {
						invite3pTraitsCacheRef.current = {}; // fail open — treat as stage 0 eligible
						fireSpotlightError('traitFetch');
					} finally {
						invite3pTraitsFetchingRef.current = false;
					}
				}

				const traits = invite3pTraitsCacheRef.current;

				const storedDismissState = check3pIn1pDismissState();
				const filteredSlackLinks = is3pInviteEligible('slack', traits, storedDismissState)
					? pastedSlackLinks
					: [];
				const filteredMailtoLinks = is3pInviteEligible('mailto', traits, storedDismissState)
					? pastedMailtoLinks
					: [];

				if (filteredSlackLinks.length === 0 && filteredMailtoLinks.length === 0) {
					return; // Both types trait-gated out — skip API calls entirely
				}

				// ── END TRAIT LAYER ──

				const slackEligibilityUrls = Array.from(new Set(filteredSlackLinks));

				const pastedEmails = filteredMailtoLinks.flatMap(getEmailsFromMailtoLink);
				try {
					const [piSettled, emailSettled] = await Promise.allSettled([
						slackEligibilityUrls.length > 0
							? Promise.allSettled(
									slackEligibilityUrls.map((url) =>
										fetchPiSlackLinkEligibleUsers(url, 'confluence'),
									),
								)
							: Promise.resolve([]),
						pastedEmails.length > 0
							? fetchEligibleEmails(pastedEmails, 'confluence')
							: Promise.resolve({}),
					]);

					const piSettledResults = piSettled.status === 'fulfilled' ? piSettled.value : [];
					const piEligibilityResults: PiEligibilityResult[] = piSettledResults.flatMap((r) =>
						r.status === 'fulfilled' ? [r.value] : [],
					);

					const hadSlackError =
						piSettled.status === 'rejected' ||
						piSettledResults.some((r) => r.status === 'rejected');
					if (hadSlackError) {
						fireSpotlightError('slackEligibility');
					}

					const eligibleEmailsResults: FetchEligibleEmailsResponse =
						emailSettled.status === 'fulfilled' ? emailSettled.value : {};

					if (emailSettled.status === 'rejected') {
						fireSpotlightError('emailEligibility');
					}

					const eligibleEmailCount = eligibleEmailsResults.eligibleEmails?.length ?? 0;
					const eligibleSlackCount = piEligibilityResults.reduce(
						(acc, result) => acc + (result?.eligibleUsers?.length ?? 0),
						0,
					);
					const hasInviteEligibleUsers = eligibleEmailCount + eligibleSlackCount > 0;

					if (
						hasInviteEligibleUsers &&
						expValEquals('twcg-853-3p-in-1p-link', 'cohort', 'variation')
					) {
						const slackDismissType = getInviteOn3pDismissType('slack', storedDismissState, traits);
						const mailtoDismissType = getInviteOn3pDismissType(
							'mailto',
							storedDismissState,
							traits,
						);

						// Adapt Confluence FlagsStateContainer to the renderer contract expected by showInviteFlags.
						const showFlag: ShowInviteFlagsArgs['showFlag'] = (args) => {
							void flags.showFlag({
								id: args.id,
								customComponent: () =>
									args.render({
										onDismiss: (flagId, dismissMethod) => {
											const key = String(flagId);
											const flagInfo = inviteFlagSourcesRef.current.get(key);
											const linkType = flagInfo?.source ?? 'mailto';
											const currentDismissType = flagInfo?.dismissType ?? 0;
											inviteFlagSourcesRef.current.delete(key);

											const dismissState =
												dismissMethod === 'dontShowAgainButton' ? 3 : currentDismissType + 1;
											const inviteType: ThirdPartyInviteType =
												linkType === 'slack' ? 'slack' : 'mailto';
											set3pIn1pDismissState(Date.now(), inviteType, dismissState);

											createEvent({
												type: 'sendUIEvent',
												data: {
													action: 'dismissed',
													actionSubject: `${linkType}InviteOn3rdPartyLinkFlag`,
													source: 'inviteOnThirdPartyLinkFlag',
													attributes: {
														twcg8533pIn1pLinkCohort: 'variation',
														growthInitiative: 'virality',
														linkType,
														product: 'confluence',
														dismissType: dismissState,
													},
													tags: ['measurement', 'growth', 'hasCustomAttributes'],
												},
											}).fire();
											void flags.hideFlag(flagId);
										},
									}),
							});
						};

						const dismissFlag = (id: string) => {
							void flags.hideFlag(id);
						};

						const shownFlags = showInviteFlags({
							cloudId,
							dismissFlag,
							eligibleEmailsResults,
							invite,
							piEligibilityResults,
							productName: 'Confluence',
							showDontShowAgain: {
								slack: slackDismissType >= 2,
								mailto: mailtoDismissType >= 2,
							},
							showFlag,
							spaceName: spaceDetail?.name ?? 'Confluence',
							onActioned: (source) => {
								createEvent({
									type: 'sendUIEvent',
									data: {
										action: 'actioned',
										actionSubject: `${source}InviteOn3rdPartyLinkFlag`,
										source: 'inviteOnThirdPartyLinkFlag',
										attributes: {
											twcg8533pIn1pLinkCohort: 'variation',
											growthInitiative: 'virality',
											linkType: source,
											product: 'confluence',
										},
										tags: ['measurement', 'growth', 'hasCustomAttributes'],
									},
								}).fire();
							},
						});

						if (shownFlags.length > 0) {
							shownFlags.forEach((flag) => {
								const flagInviteType: ThirdPartyInviteType =
									flag.source === 'slack' ? 'slack' : 'mailto';
								const currentDismissType = getInviteOn3pDismissType(
									flagInviteType,
									storedDismissState,
									traits,
								);
								inviteFlagSourcesRef.current.set(flag.flagId, {
									source: flag.source,
									dismissType: currentDismissType,
								});
								createEvent({
									type: 'sendUIEvent',
									data: {
										action: 'viewed',
										actionSubject: `${flag.source}InviteOn3rdPartyLinkFlag`,
										source: 'inviteOnThirdPartyLinkFlag',
										attributes: {
											twcg8533pIn1pLinkCohort: 'variation',
											growthInitiative: 'virality',
											linkType: flag.source,
											product: 'confluence',
										},
										tags: ['measurement', 'growth', 'hasCustomAttributes'],
									},
								}).fire();
							});
						}
					}
				} catch {
					fireSpotlightError('unknown');
				}
			}
		},
		[cloudId, flags, invite, spaceDetail?.name, createEvent, userId],
	);

	/**
	 * Save-time strategy: traverses the full ADF document to find links not
	 * present in `startingLinks`. Used by comment editors and usePublish, which
	 * already receive the full saved ADF at their natural save point.
	 */
	const handleSave = useCallback(
		(content: ADFEntity) => {
			if (!content) {
				return;
			}
			const classifiedLinks = classifyNewLinks(getAllLinks(content), initialLinksRef.current);
			fireAnalytics(classifiedLinks);
			triggerSpotlight(classifiedLinks.pastedSlackLinks, classifiedLinks.pastedMailtoLinks).catch(
				(_) => {
					//no-op
				},
			);
		},
		[fireAnalytics, triggerSpotlight],
	);

	/**
	 * Paste-time strategy: accepts a pre-extracted list of URLs from only the
	 * pasted slice (no full-document traversal). Used by the live-page editor.
	 */
	const handlePastedLinks = (pastedUrls: string[]) => {
		// Early return if no pasted links or gate is off
		if (pastedUrls.length === 0) {
			return;
		}

		// Get the starting links from the ref
		const startingLinks = initialLinksRef.current;

		const classifiedLinks = classifyNewLinks(pastedUrls, startingLinks);
		fireAnalytics(classifiedLinks);

		triggerSpotlight(classifiedLinks.pastedSlackLinks, classifiedLinks.pastedMailtoLinks).catch(
			(_) => {
				//no-op
			},
		);

		// Add new links to the initial links ref so the flag doesn't spam on duplicate links
		Object.values(classifiedLinks)
			.flat()
			.forEach((link) => initialLinksRef.current.add(link));
	};

	return { handleSave, handlePastedLinks, setInitialLinks };
};
